import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, startViewTransition } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  #updateSection() {
    // Give feedback during the network wait: our filter block runs several
    // server-side scans, so there's a gap between the click and the morphed-in
    // content with no visual cue. Dim the grid + mark it busy until the render
    // resolves.
    const busyTarget = document.getElementById(`shopify-section-${this.sectionId}`)?.querySelector('.product-grid-container');
    busyTarget?.setAttribute('aria-busy', 'true');

    // Tie the clear to renderSection's own promise (not the view-transition
    // wrapper, which resolves immediately when transitions are unsupported or
    // reduced-motion is on) so the busy state clears exactly when the fresh
    // grid morphs in, on every path.
    const render = () =>
      sectionRenderer.renderSection(this.sectionId).finally(() => busyTarget?.removeAttribute('aria-busy'));

    if (this.closest('dialog')) {
      render();
    } else {
      startViewTransition(render, ['product-grid']);
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }

  /**
   * Updates only the sort order while preserving every active filter.
   *
   * The sort control can live in a form that doesn't contain the filter
   * inputs (e.g. the mobile sort toolbar renders the `<select>` in its own
   * `facets-form-component`). Rebuilding params from that form alone via
   * `updateFilters` would serialize just `sort_by` and drop all filters — so
   * we merge the new sort value into the current URL's params instead, which
   * always reflect the active filters.
   * @param {string} sortBy - The selected sort value
   */
  updateSorting(sortBy) {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortBy);
    url.searchParams.delete('page');

    history.pushState({ urlParameters: url.searchParams.toString() }, '', url.toString());
    this.dispatchEvent(new FilterUpdateEvent(new URLSearchParams(url.search)));
    this.#updateSection();
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Filters the visible list items based on the search input (e.g. Authors filter).
   * The search input has no `name`, so it is never serialized into the facets form.
   * @param {Event} event - The input event
   */
  filterList(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    const query = event.target.value.trim().toLowerCase();
    const items = this.querySelectorAll('.facets__inputs-list-item');
    let visibleCount = 0;

    items.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      const matches = query === '' || (item.textContent ?? '').trim().toLowerCase().includes(query);
      item.classList.toggle('facets__inputs-list-item--search-hidden', !matches);
      if (matches) visibleCount += 1;
    });

    const empty = this.querySelector('[data-facet-search-empty]');
    if (empty instanceof HTMLElement) empty.hidden = visibleCount > 0;
  }

  /**
   * Prevents the search input from submitting the facets form on Enter.
   * @param {KeyboardEvent} event - The keydown event
   */
  handleSearchKeydown(event) {
    if (event.key === 'Enter') event.preventDefault();
  }

  /**
   * No-op handler so the list search input owns its `change` event and it does
   * not bubble up to the facets form's `on:change="/updateFilters"` (which would
   * trigger a needless section re-render on blur). The input has no `name`, so it
   * never affects the actual filter state.
   */
  handleSearchChange() {}

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {string} */
  currency;
  /** @type {string} */
  moneyFormat;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.currency = this.dataset.currency ?? 'USD';
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? '{{amount}}');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Extracts the placeholder from a money format string, removing currency symbols.
   * @param {string} format - The money format (e.g., "${{amount}}", "{{amount}} USD")
   * @returns {string} Just the placeholder (e.g., "{{amount}}")
   */
  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Parses a formatted money value into minor units
   * displayValue can come from user input or API response
   * @param {string} displayValue - The display value (e.g., "10.50" for USD, "9,50" for EUR, "1000" for JPY)
   * @param {string} currency - The currency code
   * @returns {number} The value in minor units
   */
  #parseDisplayValue(displayValue, currency) {
    return convertMoneyToMinorUnits(displayValue, currency) ?? 0;
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const { currency, moneyFormat } = this;
    // Parse the user's input value using currency-aware parsing
    const value = this.#parseDisplayValue(input.value, currency);

    // data-min and data-max now contain raw minor unit values (not formatted)
    const min = this.#parseDisplayValue(input.getAttribute('data-min') ?? '0', currency);
    const max = this.#parseDisplayValue(input.getAttribute('data-max') ?? '0', currency);

    if (value < min) {
      input.value = formatMoney(min, moneyFormat, currency);
    } else if (value > max) {
      input.value = formatMoney(max, moneyFormat, currency);
    }
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    let url = this.dataset.url;

    // Query chips remove only the `q` param, preserving any selected facets.
    const removeParam = this.dataset.removeParam;
    if (removeParam) {
      const next = new URL(window.location.href);
      next.searchParams.delete(removeParam);
      next.searchParams.delete('page');
      url = next.pathname + next.search;
    }

    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const activeClass = this.getAttribute('active-class') || 'active';
      clearButton.classList.toggle(activeClass, event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    // The `on:change` binding proxies `event.target` to this component, so read
    // the control that actually changed from the composed path (the native
    // <select>, or a radio in the popover variant) — same approach the event
    // framework uses internally.
    const control = event.composedPath?.()[0];
    if (!((control instanceof HTMLSelectElement || control instanceof HTMLInputElement) && control.name === 'sort_by')) {
      return;
    }

    // Merge the new sort into the current URL rather than rebuilding params
    // from this control's form — that form may not hold the filter inputs, so
    // serializing it alone would drop every active filter.
    facetsForm.updateSorting(control.value);
    this.updateFacetStatus(event);

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const currency = facetStatus.dataset.currency || '';
    const minInputNum = this.#parseCents(minInputValue, '0', currency);
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax, currency);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as minor units (cents for most currencies, but adjusted for zero-decimal currencies)
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid (formatted string like "11,400")
   * @param {string} currency - The currency code (e.g., 'USD', 'JPY', 'KRW')
   * @returns {number} The money value in minor units
   */
  #parseCents(value, fallback = '0', currency = '') {
    // Try to parse the value
    const result = convertMoneyToMinorUnits(value, currency);
    if (result !== null) return result;

    // Fall back to parsing the fallback string (which may have formatting like "11,400")
    const fallbackResult = convertMoneyToMinorUnits(fallback, currency);
    if (fallbackResult !== null) return fallbackResult;

    // Last resort: clean and parse as integer
    const cleanFallback = fallback.replace(/[^\d]/g, '');
    return parseInt(cleanFallback, 10) || 0;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const format = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return formatMoney(moneyValue, format, currency);
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Publication-year range filter: a dual-handle slider + From/To number inputs
 * that drive a set of hidden checkboxes (one per real year facet value). The
 * checkboxes are what the facets form serializes, so the URL/query behavior is
 * identical to the plain checkbox list this control replaces.
 *
 * @typedef {Object} YearRangeRefs
 * @property {HTMLInputElement} minRange - Lower slider handle
 * @property {HTMLInputElement} maxRange - Upper slider handle
 * @property {HTMLButtonElement} minText - "From" year, click-to-edit
 * @property {HTMLButtonElement} maxText - "To" year, click-to-edit
 * @property {HTMLInputElement} minInput - Inline "From" editor (hidden until editing)
 * @property {HTMLInputElement} maxInput - Inline "To" editor (hidden until editing)
 *
 * @extends {Component<YearRangeRefs>}
 */
class YearRangeComponent extends Component {
  requiredRefs = ['minRange', 'maxRange', 'minText', 'maxText', 'minInput', 'maxInput'];

  /** @type {number} Axis floor — earliest publication year in the catalog. */
  #min = 0;
  /** @type {number} Axis ceiling — the current year. */
  #max = 0;
  /** @type {number} Latest year that actually has products (for the covers-everything check). */
  #latest = 0;
  /** @type {number} Chosen lower bound — may sit on a year with no products. */
  #from = 0;
  /** @type {number} Chosen upper bound — may sit on a year with no products. */
  #to = 0;
  /** @type {boolean} Whether the in-progress inline edit should be discarded. */
  #cancelEdit = false;
  /** @type {boolean} Whether to return focus to the label after the edit ends (keyboard commit/cancel). */
  #returnFocus = false;
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  /** @type {string} The year facet's URL param name (read off the hidden checkboxes). */
  #paramName = '';
  /** @type {MutationObserver | undefined} Keeps the active-filter chip showing the chosen bounds. */
  #chipObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#min = parseInt(this.dataset.axisMin ?? '0', 10);
    this.#max = parseInt(this.dataset.axisMax ?? '0', 10);
    this.#latest = parseInt(this.dataset.latestYear ?? this.dataset.axisMax ?? '0', 10);
    this.#from = this.#clamp(parseInt(this.refs.minRange.value, 10), this.#min);
    this.#to = this.#clamp(parseInt(this.refs.maxRange.value, 10), this.#max);

    this.refs.minRange.addEventListener('input', this.#onRangeInput);
    this.refs.maxRange.addEventListener('input', this.#onRangeInput);
    this.refs.minRange.addEventListener('change', this.#onRangeChange);
    this.refs.maxRange.addEventListener('change', this.#onRangeChange);
    this.refs.minText.addEventListener('click', this.#onTextClick);
    this.refs.maxText.addEventListener('click', this.#onTextClick);
    this.refs.minInput.addEventListener('keydown', this.#onInputKeydown);
    this.refs.maxInput.addEventListener('keydown', this.#onInputKeydown);
    this.refs.minInput.addEventListener('blur', this.#onInputBlur);
    this.refs.maxInput.addEventListener('blur', this.#onInputBlur);

    // The gradient stop positions depend on the rendered track width, so repaint
    // when the layout changes (e.g. sidebar ↔ drawer) and once it's first laid out.
    this.#resizeObserver = new ResizeObserver(() => this.#paintTrack());
    this.#resizeObserver.observe(this.refs.minRange);

    this.#paramName = this.#checkboxes[0]?.name ?? '';
    document.addEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);

    // The active-filter chip is server-rendered from the matched years; keep it
    // showing the chosen bounds, re-applying whenever the morph re-renders it.
    const chipScope = this.closest('facets-drawer') ?? this.closest('.shopify-section');
    if (chipScope) {
      this.#chipObserver = new MutationObserver(() => this.#syncChip());
      this.#chipObserver.observe(chipScope, { childList: true, subtree: true });
    }

    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.minRange?.removeEventListener('input', this.#onRangeInput);
    this.refs.maxRange?.removeEventListener('input', this.#onRangeInput);
    this.refs.minRange?.removeEventListener('change', this.#onRangeChange);
    this.refs.maxRange?.removeEventListener('change', this.#onRangeChange);
    this.refs.minText?.removeEventListener('click', this.#onTextClick);
    this.refs.maxText?.removeEventListener('click', this.#onTextClick);
    this.refs.minInput?.removeEventListener('keydown', this.#onInputKeydown);
    this.refs.maxInput?.removeEventListener('keydown', this.#onInputKeydown);
    this.refs.minInput?.removeEventListener('blur', this.#onInputBlur);
    this.refs.maxInput?.removeEventListener('blur', this.#onInputBlur);
    this.#resizeObserver?.disconnect();
    this.#chipObserver?.disconnect();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#onFilterUpdate);
  }

  /**
   * The active-filter chip(s) this slider owns — the drawer instance owns the
   * chips inside the drawer; the desktop instance owns the rest (catalog toolbar).
   * @returns {HTMLElement[]}
   */
  #chipTargets() {
    const drawer = this.closest('facets-drawer');
    const root = drawer ?? this.closest('.shopify-section');
    if (!root) return [];
    const all = /** @type {HTMLElement[]} */ (Array.from(root.querySelectorAll('[data-year-chip-value]')));
    return drawer ? all : all.filter((el) => !el.closest('facets-drawer'));
  }

  /** Overwrite the chip's year text with the chosen bounds (matched-years is the server fallback). */
  #syncChip() {
    const label = this.#from === this.#to ? `${this.#from}` : `${this.#from}–${this.#to}`;
    for (const el of this.#chipTargets()) {
      if (el.textContent?.trim() !== label) el.textContent = label;
    }
  }

  /**
   * The slider position is driven entirely client-side: its controls carry
   * `data-skip-node-update` so the Section-Rendering morph leaves them alone (the
   * server only knows the matched-years range, not the chosen bounds, and would
   * otherwise snap the handles back). The one thing we still follow the server on
   * is a clear from elsewhere — chip removal / "Clear all" drops our param from
   * the new query, so reset to the full span.
   * @param {Event} event
   */
  #onFilterUpdate = (event) => {
    if (!(event instanceof FilterUpdateEvent)) return;
    if (this.#paramName && !event.detail.queryParams.has(this.#paramName)) {
      this.#from = this.#min;
      this.#to = this.#max;
      this.#render();
    }
  };

  /** @returns {HTMLInputElement[]} */
  get #checkboxes() {
    return /** @type {HTMLInputElement[]} */ (
      Array.from(this.querySelectorAll('input[type="checkbox"][data-year]'))
    );
  }

  /**
   * Clamp a year into the axis [floor, ceiling].
   * @param {number} value
   * @param {number} fallback - Used when `value` isn't a number
   * @returns {number}
   */
  #clamp(value, fallback) {
    if (Number.isNaN(value)) return fallback;
    return Math.min(Math.max(value, this.#min), this.#max);
  }

  /**
   * @param {EventTarget | null} target
   * @returns {'from' | 'to'}
   */
  #which(target) {
    return target === this.refs.minText || target === this.refs.minInput ? 'from' : 'to';
  }

  /** Drag in progress — keep the visuals/labels in sync, don't submit. */
  #onRangeInput = (/** @type {Event} */ event) => {
    this.#syncFromRanges(event.currentTarget);
    this.#render();
  };

  /** Drag released (or a keyboard step) — commit the new range. */
  #onRangeChange = (/** @type {Event} */ event) => {
    this.#syncFromRanges(event.currentTarget);
    this.#render();
    this.#commitDebounced();
  };

  /** Click the year label → edit it in place. */
  #onTextClick = (/** @type {Event} */ event) => {
    this.#startEdit(this.#which(event.currentTarget));
  };

  #onInputKeydown = (/** @type {KeyboardEvent} */ event) => {
    if (event.key === 'Enter') {
      // GET form, no submit button — stop Enter navigating; commit via blur.
      event.preventDefault();
      this.#cancelEdit = false;
      this.#returnFocus = true;
      /** @type {HTMLInputElement} */ (event.currentTarget).blur();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.#cancelEdit = true;
      this.#returnFocus = true;
      /** @type {HTMLInputElement} */ (event.currentTarget).blur();
    }
  };

  /** Leaving the inline editor commits (or discards, on Escape) and restores the label. */
  #onInputBlur = (/** @type {Event} */ event) => {
    this.#endEdit(this.#which(event.currentTarget));
  };

  // Arrow-key steps on a range input fire `change` per press; debounce so a held
  // key (or a quick drag) results in a single section re-render.
  #commitDebounced = debounce(() => this.#commit(), 300);

  /**
   * Swap a year label for its inline editor.
   * @param {'from' | 'to'} which
   */
  #startEdit(which) {
    const isFrom = which === 'from';
    const text = isFrom ? this.refs.minText : this.refs.maxText;
    const input = isFrom ? this.refs.minInput : this.refs.maxInput;

    this.#cancelEdit = false;
    input.value = String(isFrom ? this.#from : this.#to);
    text.hidden = true;
    input.hidden = false;
    input.focus();
    input.select();
  }

  /**
   * Restore the year label, committing the typed value unless the edit was cancelled.
   * @param {'from' | 'to'} which
   */
  #endEdit(which) {
    const isFrom = which === 'from';
    const text = isFrom ? this.refs.minText : this.refs.maxText;
    const input = isFrom ? this.refs.minInput : this.refs.maxInput;
    const commit = !this.#cancelEdit;

    input.hidden = true;
    text.hidden = false;

    if (commit) {
      this.#syncFromInputs(input);
      this.#commitDebounced();
    }

    this.#render();

    // Return focus to the label only for keyboard commit/cancel — not when the
    // user tabbed or clicked away.
    if (this.#returnFocus) text.focus();
    this.#cancelEdit = false;
    this.#returnFocus = false;
  }

  /**
   * Reconcile state from the slider handles. Free placement on any year within the
   * axis; the active handle is clamped only so the two never cross.
   * @param {EventTarget | null} active
   */
  #syncFromRanges(active) {
    let from = parseInt(this.refs.minRange.value, 10);
    let to = parseInt(this.refs.maxRange.value, 10);

    if (active === this.refs.minRange) {
      from = Math.min(from, to);
    } else {
      to = Math.max(to, from);
    }

    this.#from = from;
    this.#to = to;
  }

  /**
   * Reconcile state from the inline editor. A typed year is clamped into the axis
   * [earliest catalog year, current year]; any year in between is valid whether or
   * not products exist there. Ordering is enforced relative to the edited field:
   * From cannot exceed To; To cannot be less than From.
   * @param {EventTarget | null} active
   */
  #syncFromInputs(active) {
    if (active === this.refs.minInput) {
      const from = this.#clamp(parseInt(this.refs.minInput.value, 10), this.#from);
      this.#from = Math.min(from, this.#to);
    } else {
      const to = this.#clamp(parseInt(this.refs.maxInput.value, 10), this.#to);
      this.#to = Math.max(to, this.#from);
    }
  }

  /** Push current state to every control and repaint. */
  #render() {
    // Set min/max BEFORE value so the browser doesn't clamp a value past the bounds.
    for (const range of [this.refs.minRange, this.refs.maxRange]) {
      range.min = String(this.#min);
      range.max = String(this.#max);
    }
    this.refs.minRange.value = String(this.#from);
    this.refs.maxRange.value = String(this.#to);

    for (const input of [this.refs.minInput, this.refs.maxInput]) {
      input.min = String(this.#min);
      input.max = String(this.#max);
    }
    // Don't clobber an input the user is actively editing.
    if (this.refs.minInput.hidden) this.refs.minInput.value = String(this.#from);
    if (this.refs.maxInput.hidden) this.refs.maxInput.value = String(this.#to);

    this.refs.minText.textContent = String(this.#from);
    this.refs.maxText.textContent = String(this.#to);

    this.#paintTrack();
    this.#updateSummary();
    this.#syncChip();
  }

  /**
   * Position the gradient fill so its boundaries land under the thumb centers.
   * A range thumb travels only (trackWidth − thumbWidth) and is offset by half a
   * thumb at each end, so the raw value fraction has to be remapped to match.
   */
  #paintTrack() {
    const span = this.#max - this.#min || 1;
    const fromRaw = (this.#from - this.#min) / span;
    const toRaw = (this.#to - this.#min) / span;

    const trackWidth = this.refs.minRange.clientWidth;
    const thumb =
      parseFloat(getComputedStyle(this).getPropertyValue('--year-range-handle-size')) || 14;

    let fromPct = fromRaw * 100;
    let toPct = toRaw * 100;
    if (trackWidth > thumb) {
      const usable = trackWidth - thumb;
      fromPct = ((fromRaw * usable + thumb / 2) / trackWidth) * 100;
      toPct = ((toRaw * usable + thumb / 2) / trackWidth) * 100;
    }
    this.style.setProperty('--from-pct', `${fromPct}%`);
    this.style.setProperty('--to-pct', `${toPct}%`);

    // When both handles pin to the ceiling, raise the min handle so it stays grabbable.
    const atMax = this.#from >= this.#max;
    this.refs.minRange.style.zIndex = atMax ? '4' : '3';
    this.refs.maxRange.style.zIndex = atMax ? '3' : '4';
  }

  /**
   * Select every product year within [from, to]. A range that covers every year
   * that has products clears the filter (no constraint).
   */
  #commit() {
    const isFull = this.#from <= this.#min && this.#to >= this.#latest;

    let changed = false;
    for (const checkbox of this.#checkboxes) {
      const year = parseInt(checkbox.dataset.year ?? '0', 10);
      const next = !isFull && year > 0 && year >= this.#from && year <= this.#to;
      if (next !== checkbox.checked) changed = true;
      checkbox.checked = next;
    }

    // If the matched set didn't change (e.g. the handle moved across years that
    // have no products), don't round-trip — the slider already shows the chosen
    // bounds and there's nothing new to filter.
    if (!changed) return;

    const facetsForm = this.closest('facets-form-component');
    if (facetsForm instanceof FacetsFormComponent) facetsForm.updateFilters();
  }

  #updateSummary() {
    const status = this.closest('details')?.querySelector('[data-year-status]');
    if (!status) return;

    const isFull = this.#from <= this.#min && this.#to >= this.#latest;
    if (isFull) {
      status.textContent = '';
    } else {
      status.textContent = this.#from === this.#to ? `${this.#from}` : `${this.#from}–${this.#to}`;
    }
  }
}

if (!customElements.get('year-range-component')) {
  customElements.define('year-range-component', YearRangeComponent);
}

/**
 * Sort-key map for the author facet, keyed by person metaobject id.
 *
 * Liquid orders the first paint, but it can only read a capped batch of person
 * metaobjects per request (default 50, max 250 with `paginate`), and that batch
 * shifts with the paginate `?page` param on collection page 2+. So the JSON map
 * the theme emits (`<script data-author-sort-keys>`) is accumulated here and in
 * sessionStorage: a full set captured on one page keeps the order correct on the
 * others, and on stores with more authors than fit in one batch the map fills in
 * as the shopper browses. This element re-sorts after every facet re-render.
 *
 * @type {Map<string, string>}
 */
const authorSortKeys = new Map();
const AUTHOR_SORT_STORAGE_KEY = 'fdp:authorSortKeys';
let authorSortKeysHydrated = false;

/** Load any previously-captured keys from sessionStorage (once). */
function hydrateAuthorSortKeys() {
  if (authorSortKeysHydrated) return;
  authorSortKeysHydrated = true;
  try {
    const stored = sessionStorage.getItem(AUTHOR_SORT_STORAGE_KEY);
    if (!stored) return;
    for (const [id, key] of Object.entries(JSON.parse(stored))) {
      if (typeof key === 'string') authorSortKeys.set(id, key);
    }
  } catch {
    // sessionStorage unavailable or corrupt — fall back to per-page maps.
  }
}

/** Merge the keys emitted on the current page and persist the running set. */
function mergeAuthorSortKeysFromDocument() {
  let changed = false;
  for (const script of document.querySelectorAll('script[data-author-sort-keys]')) {
    let data;
    try {
      data = JSON.parse(script.textContent || '{}');
    } catch {
      continue;
    }
    for (const id in data) {
      if (typeof data[id] === 'string' && authorSortKeys.get(id) !== data[id]) {
        authorSortKeys.set(id, data[id]);
        changed = true;
      }
    }
  }
  if (!changed) return;
  try {
    sessionStorage.setItem(AUTHOR_SORT_STORAGE_KEY, JSON.stringify(Object.fromEntries(authorSortKeys)));
  } catch {
    // Persistence is best-effort; the in-memory map still works this session.
  }
}

/**
 * Reorders the author facet checkboxes alphabetically by each person's sort_key
 * ("Last, First"). The visible label keeps its "First Last" display form — only
 * the DOM order changes.
 */
class AuthorFilterSort extends HTMLElement {
  connectedCallback() {
    hydrateAuthorSortKeys();
    mergeAuthorSortKeysFromDocument();
    this.#sort();
  }

  /**
   * @param {HTMLElement} item
   * @returns {string}
   */
  #sortValue(item) {
    const id = item.dataset.authorId;
    const key = id ? authorSortKeys.get(id) : undefined;
    // Fall back to the display label (its text content) for any author missing
    // from the map, so it still lands in a sensible alphabetical position.
    return (key || item.textContent || '').trim().toLocaleLowerCase();
  }

  #sort() {
    const list = this.querySelector('[data-author-sort-list]');
    if (!list) return;

    const items = /** @type {HTMLElement[]} */ (
      Array.from(list.children).filter((el) => el instanceof HTMLElement && el.dataset.authorId != null)
    );
    if (items.length < 2) return;

    items.sort((a, b) => this.#sortValue(a).localeCompare(this.#sortValue(b)));

    const fragment = document.createDocumentFragment();
    for (const item of items) fragment.appendChild(item);
    list.appendChild(fragment);
  }
}

if (!customElements.get('author-filter-sort')) {
  customElements.define('author-filter-sort', AuthorFilterSort);
}

/**
 * Keeps the custom Contributors facet's type-to-search consistent across
 * section re-renders (author-list-filter.liquid).
 *
 * The morph resets each row's class attribute to the server render (which
 * never hides rows), and the search input carries `data-skip-node-update` so
 * the shopper's typed query survives the same morph. Left alone, those two
 * states diverge: after checking an author mid-search, the list shows
 * everything while the input still holds a query — or, worse, without this
 * element the stale hidden classes would cling to re-sorted rows and appear
 * to randomly remove authors. A MutationObserver on the list re-applies the
 * current query to the fresh rows after every morph. Live typing is still
 * handled by facet-inputs-component's /filterList; this only reconciles
 * after DOM replacements (the class toggles it makes are attribute
 * mutations, which the observer deliberately doesn't watch — no loops).
 */
class AuthorFacetSearch extends HTMLElement {
  /** @type {MutationObserver | undefined} */
  #observer;
  #scheduled = false;

  connectedCallback() {
    const list = this.querySelector('.facets__inputs-list');
    if (list) {
      this.#observer = new MutationObserver(() => this.#schedule());
      this.#observer.observe(list, { childList: true, subtree: true, characterData: true });
    }
    this.#apply();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = undefined;
  }

  /** Coalesce the observer's per-node callbacks into one pass after the morph. */
  #schedule() {
    if (this.#scheduled) return;
    this.#scheduled = true;
    queueMicrotask(() => {
      this.#scheduled = false;
      this.#apply();
    });
  }

  /** Mirror of facet-inputs-component's /filterList, driven by the preserved input value. */
  #apply() {
    const input = this.querySelector('input[data-facet-search]');
    const query = input instanceof HTMLInputElement ? input.value.trim().toLowerCase() : '';
    let visibleCount = 0;

    for (const item of this.querySelectorAll('.facets__inputs-list-item')) {
      if (!(item instanceof HTMLElement)) continue;
      const matches = query === '' || (item.textContent ?? '').trim().toLowerCase().includes(query);
      item.classList.toggle('facets__inputs-list-item--search-hidden', !matches);
      if (matches) visibleCount += 1;
    }

    const empty = this.querySelector('[data-facet-search-empty]');
    if (empty instanceof HTMLElement) empty.hidden = visibleCount > 0;
  }
}

if (!customElements.get('author-facet-search')) {
  customElements.define('author-facet-search', AuthorFacetSearch);
}
